import { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'helpers/languages/translations';

import HotTable, { HotColumn } from '@handsontable/react';
import { IList } from 'core/API/listApi';
import { sortBy, toUpper } from 'lodash';
import { IRetailer } from 'shared/types';
import { CellValue, SourceData } from 'pages/ListManagement/types';
import { arrayToKeyValue } from 'utils/arrays';
import { MNewSecondaryTargetImageHeader, translateHeaders } from './helpers/HeadersList';
import { listValidator, numberValidator, retailerValidator } from '../Validators';
import Grid from '../Grid';
import { requireTitle } from './helpers/utils';
import RetailerRenderer from './helpers/CustomDropboxRenderer';
import { AutoCompleteRenderer } from './helpers/customRenderers';

export const MNewSecondaryTargetImage: FC<{
    list: IList;
    toolId: number;
    retailers: IRetailer[];
}> = ({ list, toolId, retailers }) => {
    const translate = useTranslation();
    const hotRef = useRef<React.ElementRef<typeof HotTable> | null>(null);
    const translatedHeaders = translateHeaders(MNewSecondaryTargetImageHeader);
    const sortedRetailer = sortBy(retailers, [(o) => toUpper(o.name)]);
    const tresholdType = ['all.sources.images', 'all.retailer.images', 'defined.threshold'];

    let map_tresholdtype_to_tr: any = {};
    let map_tr_to_tresholdtype: any = {};

    const tresholdTypeTranslated = tresholdType.map((type) => {
        const translatedType = translate(type) as string;
        map_tresholdtype_to_tr[type] = translatedType;
        map_tr_to_tresholdtype[translatedType] = type;
        return translatedType;
    });

    const afterValidate = (listToValidate: IList) => {
        const newData = [...listToValidate.data].map((row) => {
            if (row[1] !== null) {
                const newRow = [...row];
                newRow[1] = map_tr_to_tresholdtype[row[1] as string];
                return newRow;
            }

            if (row[2] === null) {
                const newRow = [...row];
                newRow[2] = false;
                return newRow;
            }
            return row;
        });

        return { ...listToValidate, data: newData };
    };

    const beforeDownload = (listToDownload: CellValue[][]) => {
        const keyValueRetailers = arrayToKeyValue(retailers, 'id');

        const newData = listToDownload.map((row) => {
            const newRow = [...row];
            newRow[0] = keyValueRetailers[newRow[0] as string].name;
            newRow[1] = map_tresholdtype_to_tr[row[1] as string];
            return newRow;
        });

        return newData;
    };

    const beforeUpload = (listToUpload: SourceData) => {
        const keyValueRetailers = arrayToKeyValue(retailers, 'name');

        return listToUpload.map((data) => {
            const newRow = [...data];
            newRow[0] = keyValueRetailers[newRow[0] as string].id;
            newRow[1] = map_tr_to_tresholdtype[newRow[1] as string];

            return newRow;
        });
    };

    const formatList = (l: IList) => {
        const newData = l.data.map((data) => {
            const newRow = [...data];
            newRow[1] = map_tresholdtype_to_tr[newRow[1] as string];
            return newRow;
        });

        return {
            ...l,
            data: newData,
        };
    };

    const memoList = useMemo(() => formatList(list), [list.data, list.name]);
    
    return (
        <Grid
            toolId={toolId}
            list={memoList}
            headers={MNewSecondaryTargetImageHeader}
            beforeUpload={beforeUpload}
            paramHotRef={hotRef}
            beforeDownload={beforeDownload}
            afterValidate={afterValidate}
        >
            <HotColumn
                validator={retailerValidator(retailers)}
                title={requireTitle(translatedHeaders[0])}
                readOnly={true}
            >
                <RetailerRenderer customList={sortedRetailer} hotInstance={hotRef} hot-renderer />
            </HotColumn>
            <HotColumn
                title={requireTitle(translatedHeaders[1])}
                validator={listValidator(tresholdTypeTranslated)}
                source={tresholdTypeTranslated}
                type="autocomplete"
            >
                <AutoCompleteRenderer list={tresholdTypeTranslated} hot-renderer />
            </HotColumn>
            <HotColumn
                allowEmpty={false}
                title={requireTitle(translatedHeaders[2])}
                type="numeric"
                validator={numberValidator()}
            />
            <HotColumn
                title={translatedHeaders[3]}
                type="checkbox"
                allowEmpty
                checkedTemplate={true}
                uncheckedTemplate={false}
            />
        </Grid>
    );
};
